/* eslint-disable @typescript-eslint/naming-convention */

import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericModalService, ModalSelectElement, SelectMultiChoiceTypes } from 'src/app/shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DataField, DataFieldGroup, DataFieldTypes } from 'src/app/shared/datafields/models';
import { DataFieldGroupContainerComponent } from '../../../shared/datafields/datafield-group-container.component';
import { ArtifactAssemblyTypes, AssemblyArtifact } from 'src/app/developer/models/artifact';
import { SchedulerTaskContainer, SchedulerTaskHandler, SchedulerTaskPresentation } from '../../models/scheduler-task-objects';
import { SchedulerTaskService } from '../../services/scheduler-task.service';
import { DeveloperService } from 'src/app/developer/services/developer.service';
import { DeveloperArtifactService } from 'src/app/developer/services/developer-artifact.service';
import { ScheduleItem } from 'src/app/shared/scheduling/models';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';

import { AutoFocusDirective } from '../../../shared/auto-focus/auto-focus.directive';
import { DurationPickerComponent } from '../../../shared/duration-picker/duration-picker.component';
import { ScheduleItemComponent } from '../../../shared/scheduling/schedule-item.component';
import { DxValidationGroupComponent, DxValidationGroupModule } from 'devextreme-angular';

export enum Mode { View, Edit, New }
export enum SchedulerTaskType {Frequency, Fixed, Unknown}

@Component({
    selector: 'app-scheduler-list-details',
    templateUrl: './scheduler-task-list-details.component.html',
    styleUrls: ['./scheduler-task-list-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    SvgIconComponent,
    PermissionRequiredDirective,
    ChangeLogLinkComponent,
    ChangeLogLinkEntityDirective,
    FormsModule,
    ReactiveFormsModule,
    AutoFocusDirective,
    DurationPickerComponent,
    ScheduleItemComponent,
    DataFieldGroupContainerComponent,
    DxValidationGroupModule
]
})
export class SchedulerTaskListDetailsComponent implements OnInit {
    @ViewChild('dfgc') datafieldGroupContainer: DataFieldGroupContainerComponent;
    @ViewChild(DxValidationGroupComponent) validationGroup: DxValidationGroupComponent;

    mode = Mode.View;
    schedulerTaskType = SchedulerTaskType.Unknown;
    schedulerTaskId: number = 0;
    selectedSchedulerTask: SchedulerTaskPresentation = null;
    selectedArtifact: AssemblyArtifact = null;
    formModel: UntypedFormGroup;
    dataFieldGroups: DataFieldGroup[] = [];
    dataFieldsFormGroup = new UntypedFormGroup({});
    isLoading = false;
    schedulerArtifacts: AssemblyArtifact[] = [];
    schedulerTaskHandlers: SchedulerTaskHandler[] = [];
    useMaxRunTime: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: UntypedFormBuilder,
        private modalService: GenericModalService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private schedulerTaskService: SchedulerTaskService,
        private developerService: DeveloperService,
        private developerArtifactService: DeveloperArtifactService
    ) { }

    async ngOnInit() {
        // *** To avoid problems, keep this first
        this.formModel = this.buildFormModel();
        this.formModel.disable();

        this.route.params.pipe(
            map(e => this.convertToNumbers(e.schedulerTaskId)),
            ).subscribe(ids => {
                this.schedulerTaskId = ids.length < 1 ? 0 : ids.sort((a, b) => a - b)[0];
            });

        this.schedulerTaskHandlers = (await this.schedulerTaskService.getSchedulerTaskHandlers()).filter(s => s.IsUserAvailable).sort((a, b) => (a.Name > b.Name ? 1 : -1));

        if (this.schedulerTaskId === 0) {
            this.selectSchedulerType();
        }

        this.fcSchedulerTaskHandler.valueChanges.pipe(
            distinctUntilChanged()  // Ignore changes where the actual valueis unchange, i.e. when enabled or disabled
        ).subscribe(async e => {
            await this.refreshForSchedulerTaskConfiguration();
        });
        
        await this.refresh();

        this.formModel.statusChanges.pipe(
            distinctUntilChanged(),
            filter(e => e === 'INVALID')
        ).subscribe(e => {
            // Whenever the form becomes invalid; give the datafieldcontainer a chance to hide 'dependson' fields.
            this.datafieldGroupContainer.checkDependsOnFieldsInitial();
        });
    }

    selectSchedulerType(): void {
        let schedulerTypes: ModalSelectElement[] = [];
        schedulerTypes.push(new ModalSelectElement('Continuous run by frequency', 0));
        schedulerTypes.push(new ModalSelectElement('Run at fixed times', 1));

        this.modalService.selectMultiChoiceDirect('Select schedule type', 'Continuous run by frequency <br>Lets you choose a frequency in which the plugin will run. <br>(E.g. every 5 minutes.)<br>Triggers have a period in which the frequency is active.<br>(E.g. mon-fri 8-16) <br><br> Run at specific times <br>Lets you select specific times to run plugin.<br>(E.g. mon-fri at 1600.) <br>At least one trigger required in this mode.', schedulerTypes, schedulerTypes[0], SelectMultiChoiceTypes.DropDown).then((schedulerTypes: ModalSelectElement) => {
            const selectedType = schedulerTypes.value;
            if(selectedType === 0) {
                this.schedulerTaskType = SchedulerTaskType.Frequency;
                this.enableFrequency();
            }
            else if(selectedType === 1){
                this.schedulerTaskType = SchedulerTaskType.Fixed;
                this.disableFrequency();
            }
        }).catch(() => { this.navigateToList()});
    }

    convertToNumbers(input: string): number[] {
        const res = input.split(',').map(e => Number(e)).filter(i => i > 0);
        return res;
    }

    canDeactivate(): Promise<boolean> | boolean {
        return this.InViewMode ? true : this.askCanDeactivate();
    }


    async askCanDeactivate(): Promise<boolean> {
        try {
            await this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?');
            return true;
        }
        catch {
            return false;
        }
    }

    async refresh(): Promise<void> {
        try {
            this.isLoading = true;
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            if (this.schedulerTaskId !== 0) {
                await this.getSelectedSchedulerTask();
            }

            [
                this.schedulerArtifacts
            ] = await Promise.all([
                this.schedulerArtifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeSchedulerTaskHandler])
            ]);

            if (!this.selectedSchedulerTask) {
                this.mode = Mode.Edit;
                this.createNewSchedulerTask();
                this.formModel.enable();
            }
            else {
                this.mode = Mode.View;
                this.formModel.disable();
            }

            this.updateFormModel();
            if (this.selectedSchedulerTask.SchedulerTask.Id) {
                await this.refreshForSchedulerTaskConfiguration();
            }
        }
        catch (err) {
            console.error(err);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
            this.isLoading = false;
        }
    }

    async getSelectedSchedulerTask(): Promise<void> {
        this.selectedSchedulerTask = await this.schedulerTaskService.getSchedulerTaskPresentation(this.schedulerTaskId)
        if (this.selectedSchedulerTask.SchedulerTask.FrequencyInSeconds){
            this.schedulerTaskType = SchedulerTaskType.Frequency;
            this.enableFrequency();
        }
        else {
            this.schedulerTaskType = SchedulerTaskType.Fixed;
            this.disableFrequency();
        }
        if (this.selectedSchedulerTask?.SchedulerTask.ExecutionTimeoutInMinutes !== null){
            this.useMaxRunTime = true;
        }
        else {
            this.fcExecutionTimeInMinutes.setValue(1);
        }
    }

    createNewSchedulerTask() {
        const newDate = new Date();
        newDate.setHours(0,0,0,0);
        this.selectedSchedulerTask = new SchedulerTaskPresentation();
        this.selectedSchedulerTask.ScheduledItem = new ScheduleItem()
        this.selectedSchedulerTask.ScheduledItem.StartDate = newDate;
        this.selectedSchedulerTask.SchedulerTask.SchedulerTaskHandlerId = null;
        this.selectedSchedulerTask.SchedulerTask.FrequencyInSeconds = null;
        this.selectedSchedulerTask.SchedulerTask.ExecutionTimeoutInMinutes = null;
    }

    async refreshForSchedulerTaskConfiguration(): Promise<void> {
        const schedulerTaskHandlerId = this.fcSchedulerTaskHandler.value;
        const artifactId = this.schedulerTaskHandlers.find(s => s.Id === schedulerTaskHandlerId)?.ArtifactId;
        if (artifactId) {
            if (this.selectedSchedulerTask?.SchedulerTask.Id > 0) {
                this.dataFieldGroups = await this.schedulerTaskService.getSchedulerTaskDataFieldGroups(artifactId, this.selectedSchedulerTask?.SchedulerTask.Id);
            }
            else {
                this.dataFieldGroups = await this.schedulerTaskService.getSchedulerTaskDataFieldGroups(artifactId, null);
            }
        }
        else {
            this.dataFieldGroups = [];
        }
    }

    onEdit(_: any): void {
        this.mode = Mode.Edit;
        this.formModel.enable();
        this.datafieldGroupContainer.edit();
    }

    onCancel(_: any): void {
        if (this.schedulerTaskId !== null && this.schedulerTaskId === 0) {
            this.navigateToList();
        }
        else {
            this.mode = Mode.View;
            this.formModel.disable();
            this.refresh();
            this.datafieldGroupContainer.cancel();
        }
    }

    async onSave(_: any): Promise<void> {
        this.datafieldGroupContainer.commit();
        this.formModel.updateValueAndValidity();
        this.dataFieldsFormGroup.updateValueAndValidity();
        const validation = this.validationGroup?.instance?.validate();
        if (this.schedulerTaskType === SchedulerTaskType.Fixed){
            this.disableFrequency();
        }

        if(!this.UseMaxRunTime) {
            this.fcExecutionTimeInMinutes.setValue(1);
        }

        if (!this.formModel.valid || !this.dataFieldsFormGroup.valid || (this.isFixed && this.selectedSchedulerTask?.ScheduledItem.Triggers.length < 1) || (validation && !validation.isValid)) {
            void this.modalService.messageDirect('Validation failed', 'Please fill out all required fields.');
            return;
        }

        const fieldValues = DataFieldGroup.getDataFields(this.dataFieldGroups);
        if (fieldValues.some((fv: DataField) => {
            if (fv.DataType.DataTypeEnum === DataFieldTypes.Password || fv.DataType.DataTypeName === 'Password') {
                return fv.IsRequired && !fv.Value && !fv.SecretId;
            }
            else {
                return false;
            }
        })) {
            this.modalService.messageDirect('Validation failed', `Please fill out all required fields.`);
            return;
        }

        if (this.formModel.valid) {
            this.selectedSchedulerTask.SchedulerTask.Name = this.fcName.value;
            this.selectedSchedulerTask.SchedulerTask.Description = this.fcDescription.value;
            this.selectedSchedulerTask.ScheduledItem.Enabled = this.fcEnabled.value;
            this.selectedSchedulerTask.SchedulerTask.FrequencyInSeconds = +this.fcFrequency.value;
            this.selectedSchedulerTask.SchedulerTaskHandler = this.schedulerTaskHandlers.find(s => s.Id === this.fcSchedulerTaskHandler.value);
            this.selectedSchedulerTask.SchedulerTask.SchedulerTaskHandlerId = this.selectedSchedulerTask.SchedulerTaskHandler?.Id;

            
            if(+this.fcFrequency.value === null || +this.fcFrequency.value === 0 ) {
                this.selectedSchedulerTask.SchedulerTask.FrequencyInSeconds = null;
            }
            if(this.UseMaxRunTime && this.fcExecutionTimeInMinutes.value > 0 ) {
                this.selectedSchedulerTask.SchedulerTask.ExecutionTimeoutInMinutes = this.fcExecutionTimeInMinutes.value;
            }
            else {
                this.selectedSchedulerTask.SchedulerTask.ExecutionTimeoutInMinutes = null;
            }
            if (await this.isNameAvailable(this.selectedSchedulerTask.SchedulerTask.Name, this.selectedSchedulerTask.SchedulerTask.Id)) {
                try {
                    this.loadingPanelService.showLoadingPanelDirect('Saving...');

                    const schedulerConfigContainer: SchedulerTaskContainer = {
                        ConfigValues: DataFieldGroup.getDataFieldValues(this.dataFieldGroups, true),
                        Configuration: this.selectedSchedulerTask,
                    };

                    this.selectedSchedulerTask = await this.schedulerTaskService.saveSchedulerTask(schedulerConfigContainer);
                    await this.refreshForSchedulerTaskConfiguration(); // Make sure init-config disassembler values are loaded

                    if (this.schedulerTaskId === 0) {
                        this.mode = Mode.View;
                        this.formModel.disable();
                        this.navigateToDetails([this.selectedSchedulerTask.SchedulerTask.Id]);
                    }
                    else {
                        this.mode = Mode.View;
                        this.formModel.disable();
                    }
                }
                catch (err) {
                    console.error(err);
                }
                finally {
                    this.loadingPanelService.hideLoadingPanel();
                }
            }
            else {
                await this.modalService.messageDirect('Name not available', 'The chosen name is not available. Please choose another one.');
            }
        }
    }

    onDelete(_: any): void {
        this.modalService.confirmDirect('Delete scheduler task', 'Are you sure you want to delete the selected scheduler task?').then(() => {
            if (this.selectedSchedulerTask) {
                this.schedulerTaskService.deleteSchedulerTask(this.selectedSchedulerTask.SchedulerTask.Id).then(() => {
                    this.toastService.showToast('Scheduler task deleted...', ToastType.success);
                    this.navigateToList();
                }).catch((err) => {
                    console.error(err);
                    this.toastService.showToast('Failed to delete scheduler task...', ToastType.error);
                });
            }
        }).catch(() => { });
    }



    async isNameAvailable(schedulerName: string, schedulerId?: number): Promise<boolean> {
        try {
            return await this.schedulerTaskService.isSchedulerNameAvailable(schedulerName, schedulerId);
        }
        catch {
            return false;
        }
    }


    get isFrequency(): boolean {
        return this.schedulerTaskType === SchedulerTaskType.Frequency;
    }

    get isFixed(): boolean {
        return this.schedulerTaskType === SchedulerTaskType.Fixed;
    }

    get InViewMode(): boolean {
        return this.mode === Mode.View;
    }

    get InEditMode(): boolean {
        return this.mode === Mode.Edit;
    }

    get InNewMode(): boolean {
        return this.mode === Mode.New;
    }

    get UseMaxRunTime(): boolean {
        return this.fcUseMaxRunTime.value;
    }

    buildFormModel(): UntypedFormGroup {
        return this.fb.group({
            enabled: true,
            name: ['', {
                validators: [Validators.required],
                updateOn: 'blur'
            }],
            description: [''],
            schedulerId: [null, Validators.required],
            schedulerTaskHandlerId: [null, Validators.required],
            frequency: ['', [Validators.required, Validators.min(30)]],
            dataFieldsForm: this.dataFieldsFormGroup,
            useMaxRunTime: [null],
            executionTimeInMinutes: [1, [Validators.required, Validators.min(1)]]
        });
    }

    updateFormModel(): void {
        this.formModel.patchValue({
            enabled: this.selectedSchedulerTask?.ScheduledItem?.Enabled ?? false,
            name: this.selectedSchedulerTask?.SchedulerTask.Name ?? '',
            description: this.selectedSchedulerTask?.SchedulerTask.Description ?? '',
            schedulerId: this.selectedSchedulerTask?.SchedulerTask.Id,
            schedulerTaskHandlerId: (this.selectedSchedulerTask?.SchedulerTaskHandler?.Id) ? this.selectedSchedulerTask?.SchedulerTaskHandler?.Id : null,
            frequency: this.selectedSchedulerTask?.SchedulerTask.FrequencyInSeconds ?? '0',
            useMaxRunTime: this.useMaxRunTime,
            executionTimeInMinutes: this.selectedSchedulerTask?.SchedulerTask.ExecutionTimeoutInMinutes
        });
    }

    disableFrequency(): void {
        this.formModel.get('frequency').disable();
    }

    enableFrequency(): void {
        this.formModel.get('frequency').enable();
    }

    get fcEnabled(): UntypedFormControl {
        return this.formModel.get('enabled') as UntypedFormControl;
    }

    get fcName(): UntypedFormControl {
        return this.formModel.get('name') as UntypedFormControl;
    }

    get fcDescription(): UntypedFormControl {
        return this.formModel.get('description') as UntypedFormControl;
    }

    get fcSchedulerId(): UntypedFormControl {
        return this.formModel.get('schedulerId') as UntypedFormControl;
    }

    
    get fcSchedulerTaskHandler(): UntypedFormControl {
        return this.formModel.get('schedulerTaskHandlerId') as UntypedFormControl;
    }

    get fcFrequency(): UntypedFormControl {
        return this.formModel.get('frequency') as UntypedFormControl;
    }

    get fcTriggers(): UntypedFormControl {
        return this.formModel.get('triggers') as UntypedFormControl;
    }

    get fcUseMaxRunTime(): UntypedFormControl {
        return this.formModel.get('useMaxRunTime') as UntypedFormControl;
    }

    get fcExecutionTimeInMinutes(): UntypedFormControl {
        return this.formModel.get('executionTimeInMinutes') as UntypedFormControl;
    }


    navigateToList(): Promise<boolean> {
        return this.router.navigate(['settings/scheduler']);
    }

    navigateToDetails(schedulerTaskId: number[]): Promise<boolean> {
        return this.router.navigate(['settings/scheduler/details', { schedulerTaskId: schedulerTaskId }]);
    }

    navigateToLog(schedulerTaskId: number): Promise<boolean> {
        return this.router.navigate(['settings/scheduler/log', { schedulerTaskId: schedulerTaskId }]);
    }

}
